import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { applyPlaybook, listPlaybooks, type Playbook } from './api';

/**
 * Playbooks: the standing instructions a CS team would otherwise keep in somebody's head.
 *
 * They are not a separate kind of thing. Using one creates an ordinary automation, which then opens
 * in the automation builder like any other — so the screen says so, and takes you there. A product
 * with a "playbooks" section that hides a second, less capable editor is a product where people
 * eventually ask why their playbook cannot do what an automation can.
 *
 * Nothing here fires until somebody activates it in the builder. The card says what it would do, in
 * full, before anybody clicks: a starting point that quietly started acting on real customer
 * accounts would be the wrong kind of helpful.
 */
export function PlaybooksPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const playbooks = useQuery({ queryKey: ['playbooks'], queryFn: listPlaybooks });

  const use = useMutation({
    mutationFn: (playbook: Playbook) => applyPlaybook(playbook.key, t(`playbook.${playbook.key}`)),
    onSuccess: (automation) => navigate(`/automacoes/${automation.id}`),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (playbooks.isLoading) return <Loading />;
  if (playbooks.error) {
    return <ErrorState error={playbooks.error} onRetry={() => void playbooks.refetch()} />;
  }

  return (
    <div className="view">
      <PageHeader title={t('nav.playbooks')} description={t('playbooks.description')} />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="home-grid">
        {(playbooks.data?.playbooks ?? []).map((playbook) => (
          <Panel key={playbook.key} title={t(`playbook.${playbook.key}`)}>
            <p className="muted small">{t(`playbook.${playbook.key}.hint`)}</p>

            <dl className="facts">
              <dt>{t('playbooks.when')}</dt>
              <dd>{t(`automations.trigger.${playbook.trigger}`)}</dd>

              <dt>{t('playbooks.then')}</dt>
              <dd>
                <ul className="home-list">
                  {playbook.actions.map((action, index) => (
                    <li key={`${action}-${index}`}>{t(`automations.action.${action}`)}</li>
                  ))}
                </ul>
              </dd>
            </dl>

            <button
              type="button"
              className="button button--compact"
              disabled={use.isPending}
              onClick={() => {
                setError(null);
                use.mutate(playbook);
              }}
            >
              {t('playbooks.use')}
            </button>
          </Panel>
        ))}
      </div>

      {/* Said once, on the page, rather than in a confirmation nobody reads. */}
      <p className="muted small">{t('playbooks.inactiveNote')}</p>
    </div>
  );
}
